// Upload Contract, on the positions dashboard.
//
// Replaces the hand-rolled flow in UploadPositionFromWord, which parsed with same-line
// regexes (they match nothing in the table layout these confirmations use), booked the
// physical at the contract price instead of the board price, put the offsetting short at
// the plant instead of the parent, chose the allocation group by counterparty alone, and
// did nothing about basis.
//
// Everything here is presentation: useEfpContract does the work, so this and the chat
// entry point stay in step.
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
    AllocationModel,
    GetCustomerDataResponse,
    PositionModel,
} from '../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { StoneXFileUploader, StoneXModal } from '../../../StoneXMui';
import EfpContractReview from './EfpContractReview';
import { useEfpContract } from './useEfpContract';

export interface UploadEfpContractModalProps {
    open: boolean;
    onClose: () => void;
    /** Called after a successful post so the dashboard can refresh. */
    onSaved: () => void;
    allocations: AllocationModel[];
    positions: PositionModel[];
}

export default function UploadEfpContractModal(props: UploadEfpContractModalProps) {
    const { open, onClose, onSaved, allocations, positions } = props;
    const { enqueueSnackbar } = useSnackbar();

    const customerData = useApplicationSelector(
        (state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData
    );

    const efp = useEfpContract({ customerData, allocations, positions });

    async function handleSubmit() {
        const result = await efp.submit();

        if (result.errors.length > 0) {
            // Positions may still have been written - the message says which part failed
            // rather than claiming the whole thing did.
            result.errors.forEach(error => enqueueSnackbar(error, { variant: 'error' }));
            if (!result.contractDealTicketId) return;
        }

        enqueueSnackbar(
            `Posted the EFP: ${result.allocationCount} allocations`
            + `${result.basisReduced ? ', basis reduced' : ''}`
            + `${result.documentStored ? ', contract stored' : ''}`,
            { variant: 'success' }
        );

        efp.reset();
        onSaved();
    }

    function handleClose() {
        efp.reset();
        onClose();
    }

    return (
        <StoneXModal header="Upload Contract" open={open} onClose={handleClose} width="1200px">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {!efp.fields && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            Upload a purchase confirmation (.docx). The contract is read, checked against
                            this site's data, and shown with the positions it will write before anything
                            is posted.
                        </Typography>
                        <StoneXFileUploader
                            message="Drop the confirmation here, or click to choose one"
                            onUpload={efp.loadFile}
                        />
                    </>
                )}

                {efp.isParsing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Reading the contract...</Typography>
                    </Box>
                )}

                {efp.parseError && (
                    <Alert severity="error" variant="outlined">{efp.parseError}</Alert>
                )}

                {efp.fields && efp.resolution && (
                    <EfpContractReview
                        fileName={efp.file?.name}
                        fields={efp.fields}
                        parsedFields={efp.parsedFields ?? undefined}
                        impliedBasis={efp.impliedBasis ?? undefined}
                        resolution={efp.resolution}
                        customerData={customerData}
                        plan={efp.plan ?? undefined}
                        onChangeLeg={efp.changeLeg}
                        onChangeField={efp.changeField}
                        onChooseEntity={efp.chooseEntity}
                        onChooseBasisSource={efp.chooseBasisSource}
                        disabled={efp.isSubmitting}
                    />
                )}

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={handleClose} disabled={efp.isSubmitting}>
                        Cancel
                    </Button>
                    {efp.fields && (
                        <Button variant="outlined" onClick={efp.reset} disabled={efp.isSubmitting}>
                            Choose another file
                        </Button>
                    )}
                    <Button
                        onClick={handleSubmit}
                        disabled={!efp.canSubmit || efp.isSubmitting}
                        startIcon={efp.isSubmitting ? <CircularProgress size={16} /> : undefined}
                    >
                        {efp.isSubmitting ? 'Posting...' : 'Post EFP'}
                    </Button>
                </Box>
            </Box>
        </StoneXModal>
    );
}
